import { useCallback, useEffect, useState } from "react";
import { API } from "../../../../services/api";
import { ADMIN_API_CONFIG } from "../controleConstants";
import { firstControlePatientId, sameProductionSubcategory } from "../patientControlRules";
import { pdaCommentHistoryItems } from "../patientHistory";

function keepSelection(rows) {
    return current => (
        rows.some(row => String(row.patientId) === String(current))
            ? current
            : firstControlePatientId(rows)
    );
}

function usePatientList(ehpadId, productionSubcategory) {
    const [patients, setPatients] = useState([]);
    const [selectedPatientId, setSelectedPatientId] = useState("");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!ehpadId) {
            setPatients([]);
            setSelectedPatientId("");
            setSearch("");
            return undefined;
        }
        let mounted = true;
        const showRows = ({ data }) => {
            if (!mounted) return;
            const rows = (data || []).filter(patient => sameProductionSubcategory(patient, productionSubcategory));
            setPatients(rows);
            setSelectedPatientId(keepSelection(rows));
        };
        setLoading(true);
        API.get(`/administration/ehpads/${ehpadId}/patient-summaries`, {
            params: { status: "all" },
            noRedirectOn401: true,
        })
            .then(showRows)
            .catch(() => API.get(`/administration/ehpads/${ehpadId}/patients`, { noRedirectOn401: true })
                .then(showRows)
                .catch(() => {
                    if (!mounted) return;
                    setPatients([]);
                    setSelectedPatientId("");
                }))
            .finally(() => {
                if (mounted) setLoading(false);
            });
        return () => { mounted = false; };
    }, [ehpadId, productionSubcategory]);

    return { patients, setPatients, selectedPatientId, setSelectedPatientId, search, setSearch, loading };
}

function usePatientDetail(setPatients, onError) {
    const [actionLoading, setActionLoading] = useState(false);

    const replacePatient = useCallback(patient => {
        if (!patient?.patientId) return;
        setPatients(current => {
            const exists = current.some(row => String(row.patientId) === String(patient.patientId));
            if (!exists) return [...current, patient];
            return current.map(row => String(row.patientId) === String(patient.patientId) ? { ...row, ...patient } : row);
        });
    }, [setPatients]);

    const loadPatientDetail = useCallback(async patientId => {
        if (!patientId) return null;
        setActionLoading(true);
        onError("");
        try {
            const response = await API.get(`/administration/patients/${patientId}`, ADMIN_API_CONFIG);
            replacePatient(response.data);
            return response.data;
        } catch (requestError) {
            onError(requestError.response?.data?.message || "Impossible de charger cette fiche patient.");
            return null;
        } finally {
            setActionLoading(false);
        }
    }, [onError, replacePatient]);

    return { actionLoading, replacePatient, loadPatientDetail };
}

function usePdaHistory(selectedPatientId, loadPatientDetail) {
    const [pdaHistory, setPdaHistory] = useState([]);
    const [pdaHistoryLoading, setPdaHistoryLoading] = useState(false);
    const [pdaRemarkDraft, setPdaRemarkDraft] = useState("");

    useEffect(() => {
        setPdaHistory([]);
        setPdaRemarkDraft("");
        if (!selectedPatientId) {
            setPdaHistoryLoading(false);
            return undefined;
        }
        let mounted = true;
        setPdaHistoryLoading(true);
        loadPatientDetail(selectedPatientId).then(detail => {
            if (mounted && detail) setPdaRemarkDraft(detail.pdaProductionRemark || "");
        });
        API.get(`/administration/patients/${selectedPatientId}/history`, ADMIN_API_CONFIG)
            .then(response => {
                if (mounted) setPdaHistory(pdaCommentHistoryItems(response.data));
            })
            .catch(() => {
                if (mounted) setPdaHistory([]);
            })
            .finally(() => {
                if (mounted) setPdaHistoryLoading(false);
            });
        return () => { mounted = false; };
    }, [loadPatientDetail, selectedPatientId]);

    return { pdaHistory, pdaHistoryLoading, pdaRemarkDraft, setPdaRemarkDraft };
}

// Patients d'un EHPAD dans un dossier de contrôle : liste, sélection, fiche à jour et historique PDA.
export function useControlePatients({ ehpadId, productionSubcategory = "", searchLocked = false, onError }) {
    const list = usePatientList(ehpadId, productionSubcategory);
    const detail = usePatientDetail(list.setPatients, onError);
    const pda = usePdaHistory(list.selectedPatientId, detail.loadPatientDetail);
    const selectedPatient = list.patients.find(row => String(row.patientId) === String(list.selectedPatientId));

    const selectSuggestion = async patient => {
        if (searchLocked) return;
        const patientId = patient?.patientId || patient?.id;
        if (!patientId) return;
        list.setSearch("");
        const loaded = await detail.loadPatientDetail(patientId);
        list.setSelectedPatientId(String(loaded?.patientId || patientId));
    };

    return { ...list, ...detail, ...pda, selectedPatient, selectSuggestion };
}
